import React, { useState } from "react";
import { Button } from "react-bootstrap";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useScopeReview from "../api/useScopeReview";
import useStyles from "../styles/useStyles";
import ButtonSpinner from "./ButtonSpinner";
import EmptyState from "./EmptyState";
import GradeCard from "./GradeCard";
import MissingServicesCard from "./MissingServicesCard";
import StaleBanner from "./StaleBanner";
import TodoCard from "./TodoCard";

interface Props {
  projectId: string;
  canManage: boolean;
  canAddServices: boolean;
}

// e.g. "Nov 4, 2026, 2:14 PM"
const formatDateTime = (dateStr: string): string => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const Show: React.FC<Props> = ({ projectId, canManage, canAddServices }) => {
  const styles = useStyles();
  const {
    review,
    notReviewed,
    isLoading,
    isError,
    run,
    isRunning,
    refetch,
    saveCompleted,
    dismiss,
    undismiss,
  } = useScopeReview(projectId);
  const [runFailed, setRunFailed] = useState(false);

  const handleRun = async () => {
    const ok = await run();
    setRunFailed(ok === false);
  };

  const runToast = runFailed ? (
    <ToastAlert
      variant="warning"
      text="The scope review could not be run."
      onClose={() => setRunFailed(false)}
    />
  ) : null;

  if (isLoading) return <ScopeStackSpinner />;

  const header = (actions?: React.ReactNode) => (
    <div className={styles.header}>
      <h1>Approval Pre-check</h1>
      {actions && <div className={styles.headerActions}>{actions}</div>}
    </div>
  );

  if (notReviewed) {
    return (
      <div className={styles.page}>
        {runToast}
        {header()}
        <EmptyState
          canManage={canManage}
          isRunning={isRunning}
          onRun={handleRun}
        />
      </div>
    );
  }

  if (isError || !review) {
    return (
      <div className={styles.page}>
        {header()}
        <div className={styles.card}>
          <p>The scope review could not be loaded.</p>
          <Button
            className="btnSeawhite"
            variant="secondary"
            onClick={() => refetch()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {runToast}
      {header(
        <>
          <span className={styles.subline}>
            {`Last run ${formatDateTime(review.generated_at)}`}
          </span>
          {canManage && (
            <Button
              className="btnSeawhite"
              variant="secondary"
              onClick={handleRun}
              disabled={isRunning}
              title="Refreshing runs the review again and clears your checkmarks."
            >
              {isRunning && <ButtonSpinner />}
              Refresh Review
            </Button>
          )}
        </>
      )}
      {review.stale && <StaleBanner canManage={canManage} />}
      <GradeCard review={review} />
      <TodoCard
        review={review}
        canManage={canManage}
        saveCompleted={saveCompleted}
      />
      {review.missing_services && (
        <MissingServicesCard
          projectId={projectId}
          missingServices={review.missing_services}
          canManage={canManage}
          canAddServices={canAddServices}
          dismiss={dismiss}
          undismiss={undismiss}
          onServiceAdded={refetch}
        />
      )}
    </div>
  );
};

export default Show;
